import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Sparkles, RefreshCw, ChevronDown, ChevronUp, AlertCircle } from "lucide-react";

const SEVERITY = {
  good:      { ring: "ring-emerald-500/30", bg: "bg-emerald-500/5", chip: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300", dot: "bg-emerald-500" },
  neutral:   { ring: "ring-blue-500/30",    bg: "bg-blue-500/5",    chip: "bg-blue-500/15 text-blue-700 dark:text-blue-300",         dot: "bg-blue-500" },
  attention: { ring: "ring-amber-500/40",   bg: "bg-amber-500/5",   chip: "bg-amber-500/15 text-amber-700 dark:text-amber-300",      dot: "bg-amber-500" },
};

export default function DailyNotesCard() {
  const { t, lang } = useI18n();
  const [state, setState] = useState({ loading: true, enabled: false, summaries: [], err: null });
  const [expanded, setExpanded] = useState({});
  const [refreshing, setRefreshing] = useState({});

  const load = async () => {
    setState((s) => ({ ...s, loading: true, err: null }));
    try {
      const { data } = await api.get(`/ai/parent/daily-summaries?lang=${lang || "ar"}`);
      setState({ loading: false, enabled: !!data.enabled, summaries: data.summaries || [], err: null });
    } catch (e) {
      setState({ loading: false, enabled: false, summaries: [], err: e.response?.data?.detail || "Error" });
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [lang]);

  const regenerate = async (studentId) => {
    setRefreshing((r) => ({ ...r, [studentId]: true }));
    try {
      const { data } = await api.get(`/ai/parent/daily-summary?student_id=${studentId}&lang=${lang || "ar"}&force=true`);
      setState((s) => ({
        ...s,
        summaries: s.summaries.map((x) => (x.student_id === studentId ? data : x)),
      }));
    } catch (e) { /* swallow */ }
    setRefreshing((r) => ({ ...r, [studentId]: false }));
  };

  if (state.loading) return null;
  if (!state.enabled && state.summaries.length === 0) return null;

  return (
    <Card className="p-5 border shadow-none" data-testid="daily-notes-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 grid place-items-center text-white">
            <Sparkles className="w-4 h-4" />
          </span>
          {t("daily_notes_title")}
        </h3>
      </div>

      {state.summaries.length === 0 ? (
        <div className="text-sm text-muted-foreground text-center py-6">
          {t("daily_notes_no_kids_yet")}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {state.summaries.map((s) => {
            const sev = SEVERITY[s.severity] || SEVERITY.neutral;
            const open = expanded[s.student_id];
            return (
              <div
                key={s.student_id}
                className={`rounded-xl p-4 border ring-1 ${sev.ring} ${sev.bg} smooth`}
                data-testid={`daily-note-${s.student_id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`inline-block w-2 h-2 rounded-full ${sev.dot} mt-1.5 shrink-0`} />
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{s.student_name || "—"}</div>
                      <div className="text-[10px] text-muted-foreground font-mono">{s.date}</div>
                    </div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${sev.chip}`}>
                    {t(`daily_notes_sev_${s.severity || "neutral"}`)}
                  </span>
                </div>

                {/* Short summary */}
                <p className="text-sm leading-relaxed mb-3">{s.summary}</p>

                {/* Bullets */}
                {Array.isArray(s.bullets) && s.bullets.length > 0 && (
                  <ul className={`space-y-1.5 ${open ? "" : "max-h-[5.5rem] overflow-hidden"} relative`}>
                    {s.bullets.map((b, i) => (
                      <li key={i} className="text-xs flex items-start gap-2">
                        <span className="text-base leading-none mt-0.5">{b.icon || "•"}</span>
                        <span className="leading-relaxed">{b.text}</span>
                      </li>
                    ))}
                    {!open && s.bullets.length > 2 && (
                      <div className="absolute bottom-0 inset-x-0 h-6 bg-gradient-to-t from-background to-transparent pointer-events-none" />
                    )}
                  </ul>
                )}

                {/* Actions */}
                <div className="mt-3 flex items-center justify-between gap-1">
                  {Array.isArray(s.bullets) && s.bullets.length > 2 ? (
                    <Button
                      size="sm" variant="ghost"
                      className="text-xs h-7 px-2"
                      onClick={() => setExpanded((e) => ({ ...e, [s.student_id]: !open }))}
                      data-testid={`expand-${s.student_id}`}
                    >
                      {open ? <ChevronUp className="w-3.5 h-3.5 me-1" /> : <ChevronDown className="w-3.5 h-3.5 me-1" />}
                      {open ? t("show_less") : t("show_more")}
                    </Button>
                  ) : <span />}
                  <Button
                    size="sm" variant="ghost"
                    className="text-xs h-7 px-2 text-muted-foreground"
                    onClick={() => regenerate(s.student_id)}
                    disabled={!!refreshing[s.student_id]}
                    data-testid={`regen-${s.student_id}`}
                    title={t("daily_notes_regen")}
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${refreshing[s.student_id] ? "animate-spin" : ""}`} />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {state.err && (
        <div className="mt-3 text-xs text-amber-600 flex items-center gap-1">
          <AlertCircle className="w-3.5 h-3.5" /> {state.err}
        </div>
      )}
    </Card>
  );
}
